import React, { useEffect, useState } from "react";
import { CheckSquare, Square, Rocket, ExternalLink } from "lucide-react";
import GlassCard from "@/components/GlassCard";

// Temporary launch checklist for the Postmoro rebrand. Shipped inside the
// owner-only admin dashboard so customers never see it. Items are tracked
// in localStorage so progress survives a page reload — no backend needed
// since this list will be deleted entirely once the domain swap lands.

const STORAGE_KEY = "postmoro_rebrand_checklist_v1";

const ITEMS = [
  { id: "domain", label: "Buy/connect postmoro.com" },
  { id: "stripe-products", label: "Update Stripe business + product descriptions to Postmoro" },
  { id: "stripe-portal", label: "Update Stripe customer portal branding (logo, colors, name)" },
  { id: "x-app", label: "Update X Developer app name + callback URLs (after domain is live)" },
  { id: "meta-app", label: "Update Meta Developer app name + callback URLs (after domain is live)" },
  { id: "privacy-meta", label: "Update Privacy Policy URL in Meta App Review (after domain is live)" },
  { id: "oauth-redirects", label: "Update OAuth redirect URLs in backend env (after domain is live)" },
  { id: "support-email", label: "Create support@postmoro.com inbox + DNS / DMARC" },
  { id: "favicon", label: "Update favicon + open-graph image to Postmoro branding" },
];

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch (_e) {
    return {};
  }
}

function save(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_e) {
    /* ignore */
  }
}

export default function RebrandChecklist() {
  const [state, setState] = useState({});

  useEffect(() => {
    setState(load());
  }, []);

  const toggle = (id) => {
    setState((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      save(next);
      return next;
    });
  };

  const doneCount = ITEMS.filter((i) => state[i.id]).length;
  const total = ITEMS.length;
  const pct = total === 0 ? 0 : Math.round((doneCount / total) * 100);

  return (
    <GlassCard className="p-5" data-testid="rebrand-checklist">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] flex items-center justify-center text-[color:var(--bg-0)] shadow-[0_4px_12px_rgba(27,168,159,0.35)] shrink-0">
          <Rocket className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                Rebrand Launch Checklist
              </div>
              <div className="text-[12px] text-[color:var(--text-3)] leading-relaxed mt-0.5">
                Manual steps still required to fully cut over to Postmoro. The app
                itself is already branded — these items live outside the codebase.
              </div>
            </div>
            <div className="text-[11px] text-[color:var(--text-3)] font-mono">
              {doneCount}/{total} · {pct}%
            </div>
          </div>

          <div className="mt-3 h-1.5 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] transition-[width]"
              style={{ width: `${pct}%` }}
            />
          </div>

          <ul className="mt-4 space-y-1.5">
            {ITEMS.map((it) => {
              const done = !!state[it.id];
              const Icon = done ? CheckSquare : Square;
              return (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => toggle(it.id)}
                    data-testid={`rebrand-item-${it.id}`}
                    className={
                      "w-full text-left flex items-start gap-2 rounded-md px-2 py-1.5 transition " +
                      (done
                        ? "text-[color:var(--text-3)] line-through hover:bg-[color:var(--surface-2)]"
                        : "text-[color:var(--text-2)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--text-1)]")
                    }
                  >
                    <Icon
                      className={
                        "h-4 w-4 mt-0.5 shrink-0 " +
                        (done ? "text-[color:var(--success)]" : "text-[color:var(--text-3)]")
                      }
                    />
                    <span className="text-xs leading-relaxed">{it.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="mt-3 text-[11px] text-[color:var(--text-3)] leading-relaxed border-t border-[color:var(--border-subtle)] pt-3">
            Progress is stored locally on this admin browser. Once every item is
            checked you can delete <code className="font-mono">RebrandChecklist.js</code> +
            its mount in <code className="font-mono">AdminOverview.js</code>.{" "}
            <a
              href="https://dashboard.stripe.com/settings/branding"
              target="_blank"
              rel="noreferrer"
              className="text-[color:var(--brand-primary)] hover:underline inline-flex items-center gap-0.5"
              data-testid="rebrand-stripe-link"
            >
              Stripe branding settings <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}
